import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Plus, Eye, EyeOff, Pencil } from 'lucide-react';
import api from '../../utils/api';
import { Title, Badge, Empty } from './ui';

const TIPOS = [
  ['transferencia', 'Transferencia bancaria'],
  ['mercadopago', 'Mercado Pago'],
  ['efectivo', 'Efectivo'],
  ['debito_automatico', 'Débito automático'],
  ['exterior', 'Pago desde el exterior'],
  ['otro', 'Otro'],
];
const EJEMPLO = `Banco Galicia — Cuenta corriente en pesos
Titular: Udream SAS — CUIT 30-00000000-0
CBU: 0070000000000000000000
Alias: udream.app`;

export default function AgenciaMediosPago({ embebido = false }) {
  const [medios, setMedios] = useState(null);
  const [editando, setEditando] = useState(null);

  const cargar = () => api.get('/agency/medios-pago').then(r => setMedios(r.data)).catch(() => toast.error('No se pudo cargar'));
  useEffect(() => { cargar(); }, []);

  const guardar = async () => {
    const m = editando;
    if (!m.nombre?.trim()) return toast.error('Poné un nombre');
    try {
      if (m.id) await api.put(`/agency/medios-pago/${m.id}`, m);
      else await api.post('/agency/medios-pago', m);
      toast.success('Guardado');
      setEditando(null);
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo guardar'); }
  };

  const toggle = async (m, campo) => {
    try {
      await api.put(`/agency/medios-pago/${m.id}`, { [campo]: m[campo] ? 0 : 1 });
      cargar();
    } catch { toast.error('No se pudo guardar'); }
  };

  if (!medios) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const label = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };

  return (
    <div>
      {embebido ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800 }}>Medios de pago</h2>
          <button className="btn btn-secondary btn-sm" onClick={() => setEditando({ nombre: '', tipo: 'transferencia', datos: '', visible_cliente: 1, activo: 1 })}><Plus size={15} /> Nuevo medio</button>
        </div>
      ) : (
        <Title right={<button className="btn btn-primary" onClick={() => setEditando({ nombre: '', tipo: 'transferencia', datos: '', visible_cliente: 1, activo: 1 })}><Plus size={16} /> Nuevo medio</button>}>
          Medios de pago
        </Title>
      )}

      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18, maxWidth: 720 }}>
        Los medios marcados como <b>visibles</b> son los que ve el cliente en su portal cuando informa un pago. Los datos que escribas acá (CBU, alias, titular) se muestran tal cual, así que revisalos bien.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {medios.map(m => (
          <div key={m.id} className="card" style={{ opacity: m.activo ? 1 : 0.55 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>{m.nombre}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{(TIPOS.find(t => t[0] === m.tipo) || [])[1] || m.tipo}</div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditando({ ...m })} title="Editar"><Pencil size={15} /></button>
            </div>

            {m.datos ? (
              <pre style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', background: 'var(--bg-secondary)', padding: 10, borderRadius: 8 }}>{m.datos}</pre>
            ) : (
              <div style={{ marginTop: 10, fontSize: 12, color: '#f59e0b' }}>Sin datos cargados: el cliente no va a ver dónde pagar</div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => toggle(m, 'visible_cliente')}>
                {m.visible_cliente ? <><Eye size={14} /> Visible</> : <><EyeOff size={14} /> Oculto</>}
              </button>
              <button className="btn btn-secondary btn-sm" style={{ fontSize: 12, padding: '5px 10px' }} onClick={() => toggle(m, 'activo')}>
                {m.activo ? 'Activo' : 'Inactivo'}
              </button>
              {m.pagos > 0 && <Badge label={`${m.pagos} pagos`} />}
              {m.clientes > 0 && <Badge label={`${m.clientes} clientes`} />}
            </div>
          </div>
        ))}
        {!medios.length && <Empty>Todavía no cargaste ningún medio de pago</Empty>}
      </div>

      {editando && (
        <div className="modal-overlay" onClick={() => setEditando(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16 }}>{editando.id ? 'Editar medio de pago' : 'Nuevo medio de pago'}</h2>

            <div style={{ marginBottom: 12 }}>
              <label style={label}>Nombre</label>
              <input value={editando.nombre || ''} onChange={e => setEditando({ ...editando, nombre: e.target.value })} placeholder="Galicia pesos" />
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={label}>Tipo</label>
              <select value={editando.tipo} onChange={e => setEditando({ ...editando, tipo: e.target.value })}>
                {TIPOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={label}>Datos que ve el cliente</label>
              <textarea rows={5} value={editando.datos || ''} onChange={e => setEditando({ ...editando, datos: e.target.value })} placeholder={EJEMPLO} style={{ resize: 'vertical', fontFamily: 'inherit' }} />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!editando.visible_cliente} onChange={e => setEditando({ ...editando, visible_cliente: e.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
              Mostrarlo en el portal del cliente
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 18, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!editando.activo} onChange={e => setEditando({ ...editando, activo: e.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
              Activo
            </label>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditando(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar}>Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
